/**
 * useLineage v2 — Index-Based Lineage Computation
 * ─────────────────────────────────────────────────
 * Pure graph-walking logic for the project lineage tree.
 *
 * Performance:
 *   v1 did projects.find() inside recursive walks → O(n) per lookup × O(n) nodes = O(n²).
 *   v2 pre-builds three data structures on every projects change:
 *     - projectById  Map<id, project>       — O(1) lookup by ID
 *     - parentIndex  Map<id, id[]>          — parent IDs for each project
 *     - childIndex   Map<id, id[]>          — child IDs for each project
 *   Graph walks then use iterative BFS with Set-based visited tracking → O(k)
 *   where k = number of nodes in the connected lineage component.
 *
 * API (unchanged from v1):
 *   - computeLineage(project)    → string[] of all IDs in the tree (ancestors + descendants)
 *   - buildLineageChain(project) → Project[] sorted oldest → newest for the scrubber
 *
 * Place at: src/hooks/useLineage.js
 */

import { useCallback, useMemo } from 'react';

// ────────────────────────────────────────────────────────────────────────────
// Normalize parent IDs from either snake_case or camelCase API responses.
// ────────────────────────────────────────────────────────────────────────────

function getParentId(project) {
  return project.parent_project_id || project.parentProjectId || null;
}

function getParentIds(project) {
  return project.parent_project_ids || project.parentProjectIds || [];
}

function getCreatedAt(project) {
  return project.created_at || project.createdAt || '';
}

/**
 * Collect all parent IDs for a project, deduped.
 * Handles the legacy single parent_project_id + the multi-parent array.
 */
function allParentIdsOf(project) {
  const single = getParentId(project);
  const multi = getParentIds(project);
  if (!single && (!multi || multi.length === 0)) return [];
  const set = new Set(multi);
  if (single) set.add(single);
  return Array.from(set);
}


export default function useLineage(projects = []) {
  // ── Pre-build adjacency index whenever projects array changes ──────────
  // This is the key performance win: one O(n) pass to build the index,
  // then every computeLineage call is O(k) where k = connected component size.

  const { projectById, parentIndex, childIndex } = useMemo(() => {
    const byId = new Map();
    const parents = new Map();   // id → [parentId, ...]
    const children = new Map();  // id → [childId, ...]

    for (const p of projects) {
      byId.set(p.id, p);

      const pids = allParentIdsOf(p);
      parents.set(p.id, pids);

      // Register this project as a child of each of its parents
      for (const pid of pids) {
        if (!children.has(pid)) children.set(pid, []);
        children.get(pid).push(p.id);
      }
    }

    return { projectById: byId, parentIndex: parents, childIndex: children };
  }, [projects]);

  // ── Walk the full ancestor + descendant tree from a root project ───────
  // Iterative BFS — no recursion, no stack overflow risk on deep lineages.

  const computeLineage = useCallback((project) => {
    if (!project?.id) return [];

    const visited = new Set([project.id]);

    // Walk UP (ancestors)
    const upQueue = [project.id];
    while (upQueue.length > 0) {
      const id = upQueue.pop();
      const pids = parentIndex.get(id);
      if (pids) {
        for (const pid of pids) {
          if (!visited.has(pid)) {
            visited.add(pid);
            upQueue.push(pid);
          }
        }
      }
    }

    // Walk DOWN (descendants)
    const downQueue = [project.id];
    while (downQueue.length > 0) {
      const id = downQueue.pop();
      const cids = childIndex.get(id);
      if (cids) {
        for (const cid of cids) {
          if (!visited.has(cid)) {
            visited.add(cid);
            downQueue.push(cid);
          }
        }
      }
    }

    return Array.from(visited);
  }, [parentIndex, childIndex]);

  // ── Build the same lineage as sorted Project objects ───────────────────
  // Used by the scrubber panel: ordered oldest → newest.

  const buildLineageChain = useCallback((project) => {
    const ids = computeLineage(project);

    return ids
      .map(id => projectById.get(id))
      .filter(Boolean)
      .sort((a, b) => {
        const aDate = getCreatedAt(a);
        const bDate = getCreatedAt(b);
        if (aDate && bDate) return new Date(aDate) - new Date(bDate);
        // Fallback: sort by ID for deterministic order
        return a.id > b.id ? 1 : -1;
      });
  }, [computeLineage, projectById]);

  return { computeLineage, buildLineageChain };
}